/*  MUI  */
import { ExpandMore, OpenInNew } from '@mui/icons-material';
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Select,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, Notify, Transition } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { Requests } from 'application/Shared/Requests';
import { GlobalView } from 'application/Shared/Views';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

const csvFields = [
    { key: 'id', label: i18n.t('Common.id') },
    { key: 'category', label: i18n.t('Common.category') },
    { key: 'subCategory', label: i18n.t('Common.sub_category') },
    { key: 'risk', label: i18n.t('Common.risk') }
];

const headers = [
    {
        align: 'left',
        field: 'id',
        renderHeader: <strong>{i18n.t('Common.id')}</strong>,
        width: '30%'
    },
    {
        align: 'left',
        field: 'category',
        renderHeader: <strong>{i18n.t('Common.category')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'subCategory',
        renderHeader: <strong>{i18n.t('Common.sub_category')}</strong>,
        width: '25%'
    },
    {
        align: 'left',
        field: 'risk',
        renderHeader: <strong>{i18n.t('Common.risk')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: null,
        renderHeader: <strong>{i18n.t('Common.actions')}</strong>,
        width: '5%'
    }
];

const searchFields = ['category', 'subCategory', 'risk'];

const parameters = {
    noCache: true
};

class CategoryDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            adding: false,
            addingRisk: false,
            category: {},
            newRiskDescription: '',
            newRiskName: '',
            riskMap: {},
            risks: [],
            viewingCategory: false
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = async () => {
        const riskMap = {};

        try {
            const riskResponse = await axios.get('/risk', {
                cancelToken: this.requests.token()
            });

            if (Helpers.isset(riskResponse, 'data', 'data', 0)) {
                Object.values(riskResponse.data.data).forEach((risk) => {
                    riskMap[risk.id] = risk;
                });
                this.setState({ riskMap, risks: riskResponse.data.data });
            }
        } catch (e) {
            /* */
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    promptChanges = async (row) => {
        if (this.unmount) {
            return;
        }

        this.setState({
            adding: false,
            category: { ...row },
            viewingCategory: true
        });
    };

    updateCategory = async () => {
        const { category } = this.state;
        try {
            await axios.put(`/category/${category.id}`, {
                category: category.category,
                riskId: category.riskId,
                subCategory: category.subCategory
            });
            Notify.success('Category saved.');
            this.setState({ viewingCategory: false });
        } catch (err) {
            Notify.error(err);
        }
    };

    addCategoryPrompt = () => {
        if (this.unmount) {
            return;
        }

        this.setState({ adding: true, category: {}, viewingCategory: true });
    };

    addRiskPrompt = () => {
        if (this.unmount) {
            return;
        }

        this.setState({ addingRisk: true, category: {} });
    };

    addCategory = async () => {
        const { category } = this.state;
        try {
            await axios.post('/category/', {
                category: category.category,
                riskId: category.riskId,
                subCategory: category.subCategory
            });
            this.setState({
                adding: false,
                category: {},
                viewingCategory: false
            });
            Notify.success('Category Added.');
        } catch (err) {
            Notify.error(err);
        }
    };

    addRisk = async () => {
        const { newRiskDescription, newRiskName } = this.state;
        try {
            await axios.post('/risk', {
                description: newRiskDescription,
                name: newRiskName
            });
            Notify.success('Risk Added');
            this.setState({
                addingRisk: false,
                newRiskDescription: '',
                newRiskName: ''
            });
        } catch (err) {
            Notify.error(err);
        }
    };

    renderRow = (row) => {
        const { riskMap } = this.state;

        return (
            <>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell align="left">{row.subCategory}</TableCell>
                <TableCell align="left">
                    {riskMap[row.riskId] ? riskMap[row.riskId].name : ''}
                </TableCell>
                <TableCell align="right">
                    <span
                        onClick={() => {
                            this.promptChanges(row);
                        }}
                        aria-hidden="true"
                    >
                        <OpenInNew
                            sx={{
                                color: 'button.primary',
                                fontSize: FontSizes[1]
                            }}
                        />
                    </span>
                </TableCell>
            </>
        );
    };

    renderTable = () => (
        <ManagedTable
            csvExport
            csvExportFields={csvFields}
            csvExportFileName="global_categories"
            headers={headers}
            renderBody={this.renderRow}
            renderBodyKey="id"
            renderFooter
            rowsApi="/category"
            parameters={parameters}
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="subCategory"
        />
    );

    renderCategoryModal = () => {
        const { t } = this.props;
        const {
            adding,
            category: stateCategory,
            risks,
            viewingCategory
        } = this.state;

        return (
            <Modal
                onClose={() =>
                    this.setState({
                        viewingCategory: false
                    })
                }
                open={viewingCategory}
                aria-labelledby="simple-dialog-title"
                TransitionComponent={Transition}
            >
                <ModalBody width="100%">
                    <Box mb="16px">
                        <Typography
                            color="textSecondary"
                            style={{ fontWeight: 600 }}
                        >
                            {t('Common.category')}
                        </Typography>
                        <TextField
                            fullWidth
                            onChange={(e) =>
                                this.setState({
                                    category: {
                                        ...stateCategory,
                                        category: e.target.value
                                    }
                                })
                            }
                            value={stateCategory.category}
                        />
                    </Box>
                    <Box mb="16px">
                        <Typography
                            color="textSecondary"
                            style={{ fontWeight: 600 }}
                        >
                            {t('Common.sub_category')}
                        </Typography>
                        <TextField
                            fullWidth
                            onChange={(e) =>
                                this.setState({
                                    category: {
                                        ...stateCategory,
                                        subCategory: e.target.value
                                    }
                                })
                            }
                            value={stateCategory.subCategory}
                        />
                    </Box>
                    <Box mb="16px" width="100%">
                        <Typography
                            color="textSecondary"
                            style={{ fontWeight: 600 }}
                        >
                            {t('Common.risk')}
                        </Typography>
                        <Select
                            IconComponent={ExpandMore}
                            variant="standard"
                            fullWidth
                            defaultValue=""
                            labelId="addCategorySelectRiskLabel"
                            id="addCategorySelectRisk"
                            label="Risk"
                            value={stateCategory.riskId}
                            onChange={(e) =>
                                this.setState({
                                    category: {
                                        ...stateCategory,
                                        riskId: e.target.value
                                    }
                                })
                            }
                        >
                            {risks.map((risk) => (
                                <MenuItem
                                    key={`categoryDashboardRisk_${risk.id}`}
                                    value={risk.id}
                                >
                                    {risk.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <Button
                        data-trackid="globalAdmin_categories_catModal_close"
                        onClick={() =>
                            this.setState({
                                viewingCategory: false
                            })
                        }
                        color="primary"
                        variant="outlined"
                    >
                        {t('Common.close')}
                    </Button>
                    {adding ? (
                        <Button
                            data-trackid="globalAdmin_categories_catModal_add"
                            onClick={() => this.addCategory()}
                            color="primary"
                            variant="contained"
                        >
                            {t('Common.add')}
                        </Button>
                    ) : (
                        <Button
                            data-trackid="globalAdmin_categories_catModal_save"
                            onClick={() => this.updateCategory()}
                            color="primary"
                            variant="contained"
                        >
                            {t('Common.save')}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        );
    };

    renderAddRiskModal = () => {
        const { t } = this.props;
        const { addingRisk, newRiskDescription, newRiskName } = this.state;

        return (
            <Modal
                onClose={() =>
                    this.setState({
                        addingRisk: false,
                        newRiskDescription: '',
                        newRiskName: ''
                    })
                }
                open={addingRisk}
                aria-labelledby="simple-dialog-title"
                TransitionComponent={Transition}
            >
                <>
                    <ModalBody>
                        <Box mb="16px">
                            <Typography
                                color="textSecondary"
                                style={{ fontWeight: 600 }}
                            >
                                Risk Name
                            </Typography>
                            <TextField
                                fullWidth
                                onChange={(e) =>
                                    this.setState({
                                        newRiskName: e.target.value
                                    })
                                }
                                value={newRiskName}
                            />
                        </Box>
                        <Box mb="16px">
                            <Typography
                                color="textSecondary"
                                style={{ fontWeight: 600 }}
                            >
                                {t('Common.description')}
                            </Typography>

                            <TextField
                                fullWidth
                                onChange={(e) =>
                                    this.setState({
                                        newRiskDescription: e.target.value
                                    })
                                }
                                value={newRiskDescription}
                            />
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            data-trackid="globalAdmin_categories_riskModal_close"
                            onClick={() =>
                                this.setState({
                                    addingRisk: false,
                                    newRiskDescription: '',
                                    newRiskName: ''
                                })
                            }
                            color="primary"
                            variant="outlined"
                            sx={{ float: 'left' }}
                        >
                            {t('Common.close')}
                        </Button>
                        <Button
                            data-trackid="globalAdmin_categories_riskModal_add"
                            onClick={() => this.addRisk()}
                            color="primary"
                            variant="contained"
                        >
                            {t('Common.add')}
                        </Button>
                    </ModalFooter>
                </>
            </Modal>
        );
    };

    render() {
        const { viewingCategory, addingRisk } = this.state;

        return (
            <GlobalView page="globalAdminCategory">
                <Paper elevation={0} variant="outlined">
                    {this.renderTable()}

                    <Box mt="16px" textAlign="right" width="100%">
                        <Button
                            data-trackid="globalAdmin_categories_catModal_open"
                            onClick={() => this.addCategoryPrompt()}
                            color="primary"
                            variant="outlined"
                            style={{ marginRight: '8px' }}
                        >
                            + Add New Category
                        </Button>

                        <Button
                            data-trackid="globalAdmin_categories_riskModal_open"
                            onClick={() => this.addRiskPrompt()}
                            color="primary"
                            variant="outlined"
                        >
                            + Add New Risk
                        </Button>
                    </Box>
                    {viewingCategory && this.renderCategoryModal()}
                    {addingRisk && this.renderAddRiskModal()}
                </Paper>
            </GlobalView>
        );
    }
}

export default withTranslation()(CategoryDashboard);
